import { axiosInstance } from '.'
import { InterestIncomePreference, InvestmentStatusType_v1 } from '../lib/types/propsType_v1'
import { GetInvestmentFeeSummaryRequest, InvestService } from './apiTypes_v1'
import { removeEmptyValues } from '../lib/utils/helpers'
import {
  GET_USER_INVESTMENT_V1,
  GET_INVESTMENT_LATEST_V1,
  GET_INVESTMENT_BY_OPPORTUNITY_ID,
  ADD_OPPORTUNITY_INVESTMENT,
  UPDATE_OPPORTUNITY_INVESTMENT,
  SEARCH_INVESTMENTS_V1,
  GET_SIGNED_CLIENT_AGREEMENT_DOCUMENT,
  GET_INVESTMENT_FEE_SUMMARY,
  GET_SIGNED_TERMSHEET_AGREEMENT_DOCUMENT,
  SIGN_TERMSHEET_AGREEMENT_DOCUMENT,
  NOTIFY_INSUFFICIENT_BALANCE,
} from './endpoints'
import { InvestmentSearchParams } from './types'

export const investServices: InvestService = {
  // v1
  getUserInvestmentDetail_v1: async (headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_USER_INVESTMENT_V1, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getInvestmentLatest_v1: async (
    params: { opportunityId: number },
    headers: { [key: string]: string },
  ) => {
    try {
      const data = await axiosInstance.get(GET_INVESTMENT_LATEST_V1, {
        params: params,
        headers: headers,
      })
      return data
    } catch (error: any) {
      return error.response
    }
  },

  getInvestmentByOpportunityId: async (
    opportunityId: string,
    status: InvestmentStatusType_v1,
    headers: { [key: string]: string },
  ) => {
    try {
      const response = await axiosInstance.get(
        `${GET_INVESTMENT_BY_OPPORTUNITY_ID}/${opportunityId}/${status}`,
        {
          headers: headers,
        },
      )
      return {
        data: response.data,
        status: response.status,
      }
    } catch (error: any) {
      return error.response
    }
  },

  postOpportunityInvestment: async (
    payload: {
      projectId: string
      investmentAmount: number
      shares?: number
      assetClass?: string
      metadata: {
        completedSteps: number
        interestIncomePreference?: InterestIncomePreference
      }
    },
    headers: { [key: string]: string },
  ) => {
    try {
      // All asset classes: amount represents currency in cents (multiply by 100)
      // Backend handles conversion to shares for Listed Equity
      const amount = Math.round(payload.investmentAmount * 100)

      return await axiosInstance.post(
        ADD_OPPORTUNITY_INVESTMENT,
        {
          opportunity_id: payload?.projectId && parseInt(payload.projectId),
          amount: amount,
          ...(payload.shares ? { shares: payload.shares } : {}),
          metadata: payload?.metadata,
        },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  updateOpportunityInvestment: async (payload, headers) => {
    try {
      const metadata = { ...payload.metadata }
      delete metadata?.investmentInitiatedDate

      // All asset classes: amount represents currency in cents (multiply by 100)
      // Backend handles conversion to shares for Listed Equity
      const amount = payload?.investmentAmount
        ? Math.round(Number(payload.investmentAmount) * 100)
        : undefined

      return await axiosInstance.patch(
        UPDATE_OPPORTUNITY_INVESTMENT,
        {
          id: payload.investmentId,
          investor_id: payload?.userId && Number(payload.userId),
          opportunity_id: payload?.projectId && Number(payload.projectId),
          amount: amount,
          ...(payload.shares ? { shares: payload.shares } : {}),
          status: payload.status ?? 'INITIATED',
          metadata: metadata,
          platform_fee: payload?.platformFee,
        },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  searchInvestments_v1: async (
    params: InvestmentSearchParams,
    headers: { [key: string]: string },
    additionalQuery = '',
  ) => {
    try {
      const data = await axiosInstance.get(`${SEARCH_INVESTMENTS_V1}${additionalQuery}`, {
        params: removeEmptyValues(params),
        headers: headers,
      })
      return {
        ...data.data,
        investments: data.data.investments,
        pagination: {
          count: data.data.count,
          limit: data.data.limit,
          offset: data.data.offset,
          total: data.data.total,
        },
        status: data.status,
      }
    } catch (error: any) {
      return error.response
    }
  },

  //no need
  getSignedClientAgreementDocument: async (headers: { [key: string]: string }, opportunityId) => {
    try {
      return await axiosInstance.get(GET_SIGNED_CLIENT_AGREEMENT_DOCUMENT, {
        headers: headers,
        params: { opportunityId },
      })
    } catch (error: any) {
      return error.response
    }
  },
  //no need

  getSignedTermsheetAgreementDocument: async (
    opportunityId: string,
    investmentId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(GET_SIGNED_TERMSHEET_AGREEMENT_DOCUMENT, {
        params: { opportunityId, investmentId },
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getInvestmentFeeSummary: async (
    payload: GetInvestmentFeeSummaryRequest,
    headers: { [key: string]: string },
  ) => {
    try {
      const response = await axiosInstance.get(GET_INVESTMENT_FEE_SUMMARY, {
        params: {
          opportunityId: payload.opportunityId,
          investmentAmount: payload.investmentAmount,
          investmentId: payload.investmentId,
        },
        headers: headers,
      })
      return response.data
    } catch (error: any) {
      return error.response
    }
  },

  signTermsheetAgreement: async (opportunityId: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(
        SIGN_TERMSHEET_AGREEMENT_DOCUMENT,
        { opportunityId: opportunityId },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  notifyInsufficientBalance: async (investmentId, headers) => {
    try {
      return await axiosInstance.post(NOTIFY_INSUFFICIENT_BALANCE(investmentId), {}, { headers })
    } catch (error: any) {
      return error.response
    }
  },
}
